// ============================================================================
// Debug Chat — Shared Types & Contracts
// ============================================================================
// All types used across the debug chat module. Every component, hook, and
// utility imports from here. Do NOT define parallel types elsewhere.
// ============================================================================

// --- Circular Buffer Entries ---

export interface ConsoleLogEntry {
  timestamp: number;
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  /** Serialized arguments (safe-stringified, depth-limited) */
  args: string[];
  /** Component/module name parsed from stack trace, if available */
  source?: string;
}

export interface NetworkRequestEntry {
  timestamp: number;
  method: string;
  /** Sanitized URL (query params stripped of secrets) */
  url: string;
  /** null if request failed before response */
  status: number | null;
  durationMs: number;
  error?: string;
  /** First 200 chars, sanitized */
  requestBodyPreview?: string;
  /** First 200 chars, sanitized */
  responseBodyPreview?: string;
}

export interface WebSocketEvent {
  timestamp: number;
  type: 'open' | 'close' | 'error' | 'message_error';
  url: string;
  code?: number;
  reason?: string;
}

export interface ErrorBoundaryEvent {
  timestamp: number;
  message: string;
  stack?: string;
  /** React component stack from error boundary */
  componentStack?: string;
  /** Next.js error digest */
  digest?: string;
  /** Sentry event ID if captured */
  sentryEventId?: string;
}

export interface PerformanceEntry {
  timestamp: number;
  type: 'long_task' | 'memory_warning' | 'slow_render';
  durationMs?: number;
  detail?: string;
}

// --- Collected Runtime Context ---

export interface RuntimeContext {
  consoleLogs: ConsoleLogEntry[];
  networkRequests: NetworkRequestEntry[];
  wsEvents: WebSocketEvent[];
  errors: ErrorBoundaryEvent[];
  performance: PerformanceEntry[];
  appState: AppStateSnapshot;
  environment: EnvironmentInfo;
}

export interface AppStateSnapshot {
  wsConnectionStatus: string;
  currentRoute: string;
  walletConnected: boolean;
  /** Truncated: 0x1234...abcd */
  walletAddress?: string;
  selectedMarket?: string;
  visibleDialogs: string[];
  screenSize: { width: number; height: number };
  timestamp: number;
}

export interface EnvironmentInfo {
  userAgent: string;
  /** DEV_VERSION from AppVersion.tsx */
  buildId: string;
  /** testnet vs mainnet */
  dataEnv: string;
  nextPublicApiUrl: string;
  sentryEnabled: boolean;
  timestamp: number;
}

// --- Sanitized Context (sent to API) ---

export type SanitizedRuntimeContext = RuntimeContext;

// --- Chat Types ---

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  metadata?: {
    bugReport?: BugReport;
    agentPrompt?: string;
  };
}

export interface BugReport {
  title: string;
  severity: 'critical' | 'major' | 'minor' | 'cosmetic';
  description: string;
  reproductionSteps: string[];
  expectedBehavior: string;
  actualBehavior: string;
  capturedErrors: ErrorBoundaryEvent[];
  capturedNetworkFailures: NetworkRequestEntry[];
  environment: EnvironmentInfo;
  appStateAtTime: AppStateSnapshot;
  relevantLogs: ConsoleLogEntry[];
  /** File paths extracted from stack traces */
  affectedFiles: string[];
}

// --- API Route Types ---

export type DebugChatAction = 'chat' | 'generate_report' | 'generate_agent_prompt';

export interface DebugChatRequest {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  context: SanitizedRuntimeContext;
  action: DebugChatAction;
}

export interface DebugChatResponse {
  message: string;
  bugReport?: BugReport;
  agentPrompt?: string;
}

// --- GitHub Issue Types ---

export interface GitHubIssueRequest {
  bugReport: BugReport;
  agentPrompt: string;
  chatSummary: string;
}

export interface GitHubIssueResponse {
  issueNumber: number;
  issueUrl: string;
}

// --- Circular Buffer Config ---

export const BUFFER_SIZES = {
  consoleLogs: 200,
  networkRequests: 100,
  wsEvents: 50,
  errors: 50,
  performance: 30,
} as const;

/** Max depth for JSON.stringify of console args */
export const SERIALIZATION_DEPTH = 3;

/** Max chars per serialized console argument */
export const MAX_ARG_LENGTH = 500;

/** Max chars for request/response body previews */
export const MAX_BODY_PREVIEW_LENGTH = 200;

/** Max total payload size (bytes) sent to AI endpoint */
export const MAX_PAYLOAD_SIZE = 50_000;

// --- Sanitization Patterns ---

export const SANITIZATION_PATTERNS = {
  /** Matches hex strings >= 64 chars (private keys, signatures) */
  privateKey: /0x[a-fA-F0-9]{64,}/g,
  /** Matches Bearer tokens */
  bearerToken: /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g,
  /** Matches common API key patterns */
  apiKey: /(?:api[_-]?key|x-api-key|authorization)[=:]\s*["']?[A-Za-z0-9\-._~+/]+["']?/gi,
  /** Matches full Ethereum addresses for truncation */
  ethAddress: /0x[a-fA-F0-9]{40}/g,
} as const;
